import { useAppSelector, useAppDispatch } from "../../../../redux/store";
import { useEffect } from "react";
import { useSnackbar } from "notistack";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
} from "@mui/material";
import { format } from "date-fns";
import SyncIcon from "@mui/icons-material/Sync";
import SettingsIcon from "@mui/icons-material/Settings";

import {
  runLienWaiverSyncs,
  updateSyncSettings,
} from "../../../../redux/slices/vendorsSlice";

const SyncSettingsModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const dispatch = useAppDispatch();
  const {
    syncStatus,
    syncSettings,
    syncSettingsStatus,
    syncSettingsError,
    syncError,
    syncResult,
  } = useAppSelector((state) => state.vendors);
  const { enqueueSnackbar } = useSnackbar();

  const handleSync = () => {
    dispatch(runLienWaiverSyncs());
  };

  const handleToggleEnabled = () => {
    const newEnabledValue = !syncSettings.enabled;
    dispatch(updateSyncSettings(newEnabledValue));
  };

  // Toast notifications for sync settings updates
  useEffect(() => {
    if (syncSettingsStatus === "succeeded") {
      enqueueSnackbar(
        `Sync settings updated: ${syncSettings.enabled ? "Enabled" : "Disabled"}`,
        { variant: "success" }
      );
    }
  }, [syncSettingsStatus, syncSettings.enabled, enqueueSnackbar]);

  useEffect(() => {
    if (syncSettingsStatus === "failed" && syncSettingsError) {
      enqueueSnackbar(`Failed to update sync settings: ${syncSettingsError}`, {
        variant: "error",
      });
    }
  }, [syncSettingsStatus, syncSettingsError, enqueueSnackbar]);

  // Toast notifications for manual sync operations
  useEffect(() => {
    if (syncStatus === "succeeded" && syncResult) {
      const result = syncResult as { summary?: string };
      enqueueSnackbar(result.summary || "Manual sync completed successfully", {
        variant: "success",
      });
    }
  }, [syncStatus, syncResult, enqueueSnackbar]);

  useEffect(() => {
    if (syncStatus === "failed" && syncError) {
      enqueueSnackbar(`Manual sync failed: ${syncError}`, {
        variant: "error",
      });
    }
  }, [syncStatus, syncError, enqueueSnackbar]);

  const formatLastSyncDate = (dateString: any) => {
    if (!dateString) return "Never";
    try {
      return format(new Date(dateString), "MMM dd, yyyy 'at' HH:mm");
    } catch {
      return "Invalid date";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 0 },
      }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <SettingsIcon />
        Sync Settings
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Enable/Disable Switch */}
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={syncSettings.enabled}
                  onChange={handleToggleEnabled}
                  disabled={syncSettingsStatus === "loading"}
                  color="primary"
                />
              }
              label="Enable Automatic Sync"
              sx={{
                "& .MuiFormControlLabel-label": {
                  fontSize: "1rem",
                  fontWeight: 500,
                },
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              When enabled, lien waivers will be automatically synchronized at
              regular intervals.
            </Typography>
          </Box>

          <Divider />

          {/* Last Sync Date */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Last Sync Date
            </Typography>
            <Typography variant="body1">
              {formatLastSyncDate(syncSettings.lastSyncDate)}
            </Typography>
            {syncSettings.lastSyncType && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Type:{" "}
                {syncSettings.lastSyncType === "auto" ? "Automatic" : "Manual"}
              </Typography>
            )}
          </Box>

          <Divider />

          {/* Manual Sync Button */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Manual Sync
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sync the accounting book the portal shows — Intuit — now, instead
              of waiting for the automatic sync. The result says what each
              connected company answered.
            </Typography>
            <Button
              variant="contained"
              startIcon={<SyncIcon />}
              onClick={handleSync}
              disabled={syncStatus === "loading"}
              sx={{
                borderRadius: 0,
              }}
            >
              {syncStatus === "loading" ? "Syncing..." : "Sync Now"}
            </Button>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} sx={{ }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SyncSettingsModal;
